import { Copy, DotsThree, FlowArrow, PencilSimple, Play, Plus } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import {
  useDeleteFlow,
  useFlow,
  useFlows,
  useFlowVersions,
  useSaveFlow,
  useSetFlowStatus,
} from "~/api/queries";
import { ABILITY, type Flow, type FlowStatus } from "~/api/types";
import { Field, Pill, Row, Rows, StatusPill } from "~/features/automation/components";
import { formatCompact } from "~/features/automation/format";
import { duplicateFlow, freeSlug, scaffoldFlow } from "~/features/flows/definition";
import {
  FLOW_PURPOSE_LABELS,
  FlowLibraryFilter,
  type FlowPurpose,
  flowPurpose,
} from "~/features/flows/FlowLibrary";
import { RunFlowDialog } from "~/features/flows/RunFlowDialog";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

/** Draft is the idle state, Archived the retired one; the two in between are the live ones. */
const NEXT_STATUSES: FlowStatus[] = ["draft", "active", "paused", "archived"];

function FlowDetailDialog({ flow, onClose }: { flow: Flow; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { data: detail, isLoading } = useFlow(flow.slug);
  const { data: versions } = useFlowVersions(flow.slug);

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={flow.name}
      closeLabel={t("Close")}
      size="xl"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t("Done")}
        </Button>
      }
    >
      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : (
        <div className="flex flex-col gap-4">
          {detail?.description ? <Text variant="small">{detail.description}</Text> : null}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label={t("Identifier")}>
              <span className="font-mono text-small">{flow.slug}</span>
            </Field>
            <Field label={t("Status")}>
              <StatusPill status={flow.status} />
            </Field>
            <Field label={t("Version")}>{detail ? `v${detail.version}` : "—"}</Field>
          </div>

          <div className="flex flex-col gap-1.5">
            <Text variant="small">{t("Steps")}</Text>
            <Rows>
              {detail?.nodes.map((node, index) => (
                <Row key={node.id}>
                  <span className="w-5 shrink-0 text-small text-text-tertiary tabular-nums">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body text-text">{node.name}</p>
                    <p className="truncate font-mono text-small text-text-tertiary">{node.id}</p>
                  </div>
                  {node.requireApproval ? <Pill tone="warning">{t("Needs approval")}</Pill> : null}
                  {node.model ? <Pill>{node.model}</Pill> : null}
                </Row>
              ))}
            </Rows>
          </div>

          <div className="flex flex-col gap-1.5">
            <Text variant="small">
              {/* Editing a flow always makes a new version, so a run can always say what produced it. */}
              {t("Versions")}
            </Text>
            <Rows>
              {versions?.slice(0, 6).map((version) => (
                <Row key={version.version}>
                  <span className="w-10 shrink-0 text-small text-text tabular-nums">
                    v{version.version}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-small text-text-secondary">
                      {version.note ?? t("No note")}
                    </p>
                    <p className="truncate text-small text-text-tertiary">{version.createdBy}</p>
                  </div>
                  {version.isHead ? <Pill tone="accent">{t("Current")}</Pill> : null}
                  <span className="hidden text-small text-text-tertiary sm:inline">
                    {formatRelativeTime(version.createdAt, i18n.language)}
                  </span>
                </Row>
              ))}
            </Rows>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/**
 * Making a flow. There is no `ai-flows.create` — a flow comes into being the first time a
 * definition is SAVED on a slug nothing is using, so this dialog collects a name, mints a free
 * slug with the client mirror of the server's own normalizer, and saves a one-node scaffold.
 * The person lands in the studio with something real to edit rather than an empty canvas.
 */
function NewFlowDialog({ taken, onClose }: { taken: string[]; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const saveFlow = useSaveFlow();
  const navigate = useNavigate();
  const slug = name.trim() === "" ? "" : freeSlug(name, taken);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (slug === "") {
      return;
    }
    saveFlow.mutate(
      { slug, definition: scaffoldFlow(name.trim(), slug), note: t("created") },
      {
        onSuccess: (result) => {
          if (!result.ok) {
            toast.error(result.errors[0] ?? t("The flow could not be created"));
            return;
          }
          onClose();
          void navigate({ to: "/flows/$slug/edit", params: { slug } });
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("New flow")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={slug === "" || saveFlow.isPending} onClick={submit}>
            {t("Create")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input
          label={t("Name")}
          value={name}
          fullWidth
          onChange={(event) => setName(event.target.value)}
          placeholder={t("What should this flow be called?")}
        />
        <Field label={t("Identifier")}>
          <span className="font-mono text-small">{slug || "—"}</span>
        </Field>
        <Text variant="small">
          {t(
            "The identifier is derived from the name and is what every run, schedule and link uses.",
          )}
        </Text>
      </form>
    </Dialog>
  );
}

/**
 * The workspace's flows: what exists, what state each is in, and the things a person actually
 * does here — make one, open it, start one, and change whether it may start itself.
 */
export function Flows() {
  const { t, i18n } = useTranslation();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [library, setLibrary] = useState<"all" | FlowPurpose>("all");
  const [running, setRunning] = useState<Flow | null>(null);
  const [inspecting, setInspecting] = useState<Flow | null>(null);
  const [deleting, setDeleting] = useState<Flow | null>(null);
  const [creating, setCreating] = useState(false);

  const api = useApi();
  const { data, isLoading } = useFlows(includeArchived);
  const setStatus = useSetFlowStatus();
  const deleteFlow = useDeleteFlow();
  const saveFlow = useSaveFlow();
  const navigate = useNavigate();

  const flows = data?.flows ?? [];
  const visibleFlows = flows.filter((flow) => library === "all" || flowPurpose(flow) === library);
  const canCreate = flows.some((flow) => flow.abilities[ABILITY.flowUpdate]);
  const overLimit = (data?.activeCount ?? 0) > (data?.activeLimit ?? 0);

  /**
   * Duplicate = read the whole definition, re-identify it, save it on a free slug. Reading the
   * definition rather than rebuilding from the summary is the point: a copy that quietly loses
   * sources, layout and every field slate does not render is worse than no copy at all.
   */
  const duplicate = async (flow: Flow) => {
    const { definition } = await api.automation.flows.definition(flow.slug);
    if (!definition) {
      toast.error(t("That flow's definition could not be read, so it cannot be copied"));
      return;
    }
    const name = t("{{name}} (copy)", { name: flow.name });
    const slug = freeSlug(
      name,
      flows.map((entry) => entry.slug),
    );
    saveFlow.mutate(
      {
        slug,
        definition: duplicateFlow(definition as Parameters<typeof duplicateFlow>[0], slug, name),
        note: t("duplicated from {{slug}}", { slug: flow.slug }),
      },
      {
        onSuccess: (result) => {
          if (!result.ok) {
            toast.error(result.errors[0] ?? t("The copy could not be saved"));
            return;
          }
          void navigate({ to: "/flows/$slug/edit", params: { slug } });
        },
      },
    );
  };

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const menuFor = (flow: Flow): MenuItem[] => {
    const items: MenuItem[] = [];
    if (flow.abilities[ABILITY.flowUpdate]) {
      items.push({
        type: "button",
        title: t("Open the editor"),
        icon: PencilSimple,
        onClick: () => void navigate({ to: "/flows/$slug/edit", params: { slug: flow.slug } }),
      });
      items.push({
        type: "button",
        title: t("Duplicate"),
        icon: Copy,
        onClick: () => void duplicate(flow),
      });
    }
    if (flow.abilities[ABILITY.flowRun]) {
      items.push({
        type: "button",
        title: t("Run now"),
        icon: Play,
        onClick: () => setRunning(flow),
      });
    }
    if (flow.abilities[ABILITY.flowUpdate]) {
      items.push({ type: "separator" });
      items.push({ type: "heading", title: t("Status") });
      for (const status of NEXT_STATUSES) {
        items.push({
          type: "button",
          title: statusLabel(status, t),
          selected: flow.status === status,
          disabled: flow.status === status,
          onClick: () => setStatus.mutate({ slug: flow.slug, status }),
        });
      }
    }
    if (flow.abilities[ABILITY.flowDelete] && flow.status === "archived") {
      // Deleting is offered only where the server permits it: archive first, delete second.
      items.push({ type: "separator" });
      items.push({
        type: "button",
        title: t("Delete"),
        dangerous: true,
        onClick: () => setDeleting(flow),
      });
    }
    return items;
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={overLimit ? "text-danger text-small" : "text-small text-text-secondary"}>
            {t("{{active}} of {{limit}} flows are active in this workspace.", {
              active: data?.activeCount ?? 0,
              limit: data?.activeLimit ?? 0,
            })}
          </span>
          {/* The limit is REPORTED, not enforced — on either side (W3 §F-1). Saying so is more
              useful than blocking activation here would be, because the rule does not live in
              this client and pretending otherwise would be a lie about where it does live. */}
          {overLimit ? (
            <span className="text-small text-text-tertiary">
              {t(
                "Over the policy limit. Nothing stops you — the limit exists so a scheduled flow you forgot about cannot keep spending.",
              )}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <div className="min-w-48">
            <Switch
              checked={includeArchived}
              onCheckedChange={setIncludeArchived}
              label={t("Show archived")}
            />
          </div>
          {canCreate ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              {t("New flow")}
            </Button>
          ) : null}
        </div>
      </div>

      <FlowLibraryFilter flows={flows} value={library} onChange={setLibrary} />
      {visibleFlows.length === 0 ? (
        <Empty
          icon={FlowArrow}
          message={t(
            flows.length === 0 ? "No flows in this workspace yet" : "No flows in this category",
          )}
        />
      ) : (
        <Rows>
          {visibleFlows.map((flow) => {
            const items = menuFor(flow);
            return (
              <Row key={flow.id}>
                {flow.abilities[ABILITY.flowUpdate] ? (
                  <Link
                    to="/flows/$slug/edit"
                    params={{ slug: flow.slug }}
                    className="min-w-0 flex-1"
                  >
                    <p className="truncate text-body text-text">{flow.name}</p>
                    <p className="truncate font-mono text-small text-text-tertiary">{flow.slug}</p>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => setInspecting(flow)}
                    className="min-w-0 flex-1 cursor-pointer text-start"
                  >
                    <p className="truncate text-body text-text">{flow.name}</p>
                    <p className="truncate font-mono text-small text-text-tertiary">{flow.slug}</p>
                  </button>
                )}
                <Pill>{FLOW_PURPOSE_LABELS[flowPurpose(flow)]}</Pill>
                <StatusPill status={flow.status} />
                <span className="hidden w-20 text-small text-text-tertiary sm:inline">
                  {t("{{count}} steps", { count: flow.nodeCount })}
                </span>
                <span className="hidden w-10 text-small text-text-tertiary md:inline">
                  v{formatCompact(flow.versionCount, i18n.language)}
                </span>
                <span className="hidden w-24 text-small text-text-tertiary md:inline">
                  {formatRelativeTime(flow.updatedAt, i18n.language)}
                </span>
                <span className="hidden w-28 truncate text-small text-text-tertiary lg:inline">
                  {flow.entityLinkKind === "workspace" ? t("Workspace") : flow.entityLinkKind}
                </span>
                <span className="hidden w-32 truncate text-small text-text-tertiary lg:inline">
                  {flow.categories.length > 0 ? flow.categories.join(", ") : "—"}
                </span>
                {items.length > 0 ? (
                  <Menu
                    ariaLabel={t("Flow actions")}
                    align="end"
                    items={items}
                    trigger={
                      <IconButton icon={DotsThree} aria-label={t("Flow actions")} size="sm" />
                    }
                  />
                ) : null}
              </Row>
            );
          })}
        </Rows>
      )}

      {creating ? (
        <NewFlowDialog taken={flows.map((flow) => flow.slug)} onClose={() => setCreating(false)} />
      ) : null}
      {running ? (
        <RunFlowDialog slug={running.slug} name={running.name} onClose={() => setRunning(null)} />
      ) : null}
      {inspecting ? (
        <FlowDetailDialog flow={inspecting} onClose={() => setInspecting(null)} />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          title={t("Delete flow")}
          description={t("“{{name}}” and every version of it are removed for good.", {
            name: deleting.name,
          })}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          closeLabel={t("Close")}
          dangerous
          busy={deleteFlow.isPending}
          onConfirm={() =>
            deleteFlow.mutate(deleting.slug, {
              onSuccess: () => {
                setDeleting(null);
                toast.success(t("Flow deleted"));
              },
            })
          }
        />
      ) : null}
    </div>
  );
}

function statusLabel(status: FlowStatus, t: (key: string) => string): string {
  switch (status) {
    case "active":
      return t("Active");
    case "paused":
      return t("Paused");
    case "archived":
      return t("Archived");
    default:
      return t("Draft");
  }
}
